import { ApiError } from '../identity/types';
import { CorrectionNotificationState, DocumentReviewStatus, DocumentSource, EmployeeDocumentType } from '../reports/types';
import type { EmployeeDocumentsGateway } from './EmployeeDocumentsGateway';
import { ACCEPTED_DOCUMENT_TYPES, MAX_DOCUMENT_BYTES, type EmployeeDocumentResponse, type UploadDocumentInput } from './types';

const nowIso = () => new Date().toISOString();

/** In-memory stand-in that applies the same rules as EPN.Employees.Api, so the screen behaves the same way in mock mode: the file checks, Other needing a
 * custom type, archive refused for an employee's own upload, a reason needed for W-4 and I-9, and a correction request kept once at a time. Synthetic data only. */
export class MockEmployeeDocumentsGateway implements EmployeeDocumentsGateway {
  private documents: EmployeeDocumentResponse[] = [
    // One employee-submitted file for the demo employee, so the employee-origin actions can be seen. Synthetic.
    {
      id: 'doc-demo-employee-i9', employeeId: 'e1', documentType: EmployeeDocumentType.I9, customTypeDescription: null, documentName: 'Employee-submitted I-9',
      expiryDate: null, originalFileName: 'i9-synthetic.pdf', contentType: 'application/pdf', sizeBytes: 182_000, uploadedByUserId: 'employee-user',
      uploadedAtUtc: '2026-10-01T15:00:00.000Z', source: DocumentSource.Employee, reviewStatus: DocumentReviewStatus.PendingReview,
      correctionReason: null, correctionRequestedAtUtc: null, correctionNotification: null,
    },
  ];

  private failNext: string | null = null;
  private emailWorks = true;

  async list(employeeId: string): Promise<EmployeeDocumentResponse[]> {
    return this.documents
      .filter((d) => d.employeeId === employeeId)
      .sort((a, b) => b.uploadedAtUtc.localeCompare(a.uploadedAtUtc));
  }

  async upload(employeeId: string, input: UploadDocumentInput): Promise<EmployeeDocumentResponse> {
    if (this.failNext) {
      const message = this.failNext;
      this.failNext = null;
      throw new ApiError(500, message);
    }
    if (!ACCEPTED_DOCUMENT_TYPES[input.file.type]) throw new ApiError(400, `Content type '${input.file.type}' is not allowed. Allowed types: PDF, PNG, JPEG.`);
    if (input.file.size <= 0) throw new ApiError(400, 'The file is empty.');
    if (input.file.size > MAX_DOCUMENT_BYTES) throw new ApiError(400, 'The file exceeds the 10 MB limit.');
    if (!input.documentName.trim()) throw new ApiError(400, 'Document Name is required.');
    if (input.documentType === EmployeeDocumentType.Other && !input.customTypeDescription?.trim()) {
      throw new ApiError(400, 'Custom Document Type is required when Document Type is Other.');
    }
    const created: EmployeeDocumentResponse = {
      id: crypto.randomUUID(), employeeId, documentType: input.documentType,
      customTypeDescription: input.documentType === EmployeeDocumentType.Other ? input.customTypeDescription!.trim() : null,
      documentName: input.documentName.trim(), expiryDate: input.expiryDate, originalFileName: input.file.name, contentType: input.file.type,
      sizeBytes: input.file.size, uploadedByUserId: 'admin-user', uploadedAtUtc: nowIso(), source: DocumentSource.Admin,
      reviewStatus: DocumentReviewStatus.PendingReview, correctionReason: null, correctionRequestedAtUtc: null, correctionNotification: null,
    };
    this.documents = [created, ...this.documents];
    return created;
  }

  async archive(employeeId: string, documentId: string, reason: string | null): Promise<void> {
    const doc = this.documents.find((d) => d.id === documentId && d.employeeId === employeeId);
    if (!doc) throw new ApiError(404, 'Document not found.');
    if (doc.source === DocumentSource.Employee) {
      throw new ApiError(409, 'A document the employee uploaded cannot be removed here. Request a correction instead.');
    }
    if ((doc.documentType === EmployeeDocumentType.W4 || doc.documentType === EmployeeDocumentType.I9) && !reason?.trim()) {
      throw new ApiError(400, 'A reason is required to remove a W-4 or I-9 document.');
    }
    this.documents = this.documents.filter((d) => d.id !== documentId);
  }

  async requestCorrection(employeeId: string, documentId: string, reason: string): Promise<EmployeeDocumentResponse> {
    const doc = this.documents.find((d) => d.id === documentId && d.employeeId === employeeId);
    if (!doc) throw new ApiError(404, 'Document not found.');
    if (doc.source !== DocumentSource.Employee) throw new ApiError(409, 'A correction can only be requested for a document the employee uploaded.');
    if (doc.reviewStatus === DocumentReviewStatus.NeedsCorrection) throw new ApiError(409, 'A correction has already been requested for this document.');
    if (!reason.trim()) throw new ApiError(400, 'A reason is required to request a correction.');
    const updated: EmployeeDocumentResponse = {
      ...doc, reviewStatus: DocumentReviewStatus.NeedsCorrection, correctionReason: reason.trim(), correctionRequestedAtUtc: nowIso(),
      correctionNotification: this.emailWorks ? CorrectionNotificationState.Sent : CorrectionNotificationState.NotSent,
    };
    this.documents = this.documents.map((d) => (d.id === documentId ? updated : d));
    return updated;
  }

  downloadUrl(employeeId: string, documentId: string): string {
    return `#mock-download/${employeeId}/${documentId}`;
  }

  /** Test helper: the next upload fails with this message (a server-side failure), so the retry path can be exercised. */
  failNextUploadForTests(message = 'The upload failed. Nothing was saved.'): void {
    this.failNext = message;
  }

  /** Test helper: the notification email could not be delivered. */
  setEmailWorksForTests(works: boolean): void {
    this.emailWorks = works;
  }

  /** Test helper: add a document directly (for example an old one with an expiry date). */
  seedForTests(doc: Partial<EmployeeDocumentResponse> & Pick<EmployeeDocumentResponse, 'employeeId' | 'documentType'>): EmployeeDocumentResponse {
    const full: EmployeeDocumentResponse = {
      id: crypto.randomUUID(), customTypeDescription: null, documentName: 'Seeded document', expiryDate: null, originalFileName: 'seeded.pdf',
      contentType: 'application/pdf', sizeBytes: 100_000, uploadedByUserId: 'admin-user', uploadedAtUtc: nowIso(), source: DocumentSource.Admin,
      reviewStatus: DocumentReviewStatus.PendingReview, correctionReason: null, correctionRequestedAtUtc: null, correctionNotification: null, ...doc,
    };
    this.documents = [full, ...this.documents];
    return full;
  }

  /** Test helper: forget everything except the demo document. */
  resetForTests(): void {
    this.documents = this.documents.filter((d) => d.id === 'doc-demo-employee-i9').map((d) => ({
      ...d, reviewStatus: DocumentReviewStatus.PendingReview, correctionReason: null, correctionRequestedAtUtc: null, correctionNotification: null,
    }));
    this.failNext = null;
    this.emailWorks = true;
  }
}
